import { useCallback, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { z } from 'zod'

import { useForm } from '@/lib/react-hook-form'
import { isAxiosError } from '@/lib/react-query'
import type { ContactReqbody } from '@/server/contact/CreateContactMessage/type'

import { UseSendContactMutation } from '../api/sendContact'

export type UseSendContact = ReturnType<typeof useSendContact>

type Props = {
  onSuccess: () => void
  onError: () => void
}

const contactSchema = z
  .object({
    company_name: z.string().nonempty('会社名を入力してください'),
    position: z.string().or(z.enum([''])),
    department_name: z.string().or(z.enum([''])),
    company_url: z.string().or(z.enum([''])),
    name: z.string().nonempty('氏名を入力してください'),
    name_kana: z.string().nonempty('氏名（ふりがな）を入力してください'),
    email: z.string().email('メールアドレスを入力してください'),
    message: z.string().nonempty('メッセージを入力してください'),
    phone_number: z
      .string()
      .regex(/^0+(\d)*$/, {
        message: '0から始まる半角数字(ハイフンなし)を入力してください',
      })
      .min(10, '10桁以上で入力してください')
      .max(11, '11桁以下で入力してください')
      .nonempty('電話番号を入力してください'),
    contact_type: z.string().nonempty('お問い合わせ種別を選択してください'),
    agree: z.string().refine((value) => !!value, {
      message: '同意してください',
      path: ['agree'],
    }),
  })
  .transform(({ agree, ...values }) => values)

export const useSendContact = ({ onSuccess, onError }: Props) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [errorMessage, setErrorMessage] = useState('')
  const form = useForm({
    schema: contactSchema,
  })

  const mutation = UseSendContactMutation({
    onSuccess: () => {
      onSuccess?.()
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        setErrorMessage(error.response.data.message.toString() ?? '')
        onError?.()
      }
    },
  })

  const getRecaptchaToken = useCallback(async () => {
    if (!executeRecaptcha) return
    const token = await executeRecaptcha('contact')
    return token
  }, [executeRecaptcha])

  const onSubmit = form.handleSubmit(async (params: ContactReqbody) => {
    const token = await getRecaptchaToken()
    mutation.mutateAsync({
      token,
      ...params,
    })
  })

  const isDisabled = mutation.isLoading || !form.formState.isValid

  return {
    form,
    isDisabled,
    errorMessage,
    onSubmit,
  }
}
